import { activeSymbols, type FireRedSymbols } from './symbols';

// Sprites de Pokémon tirados da ROM do jogador: foto de frente (64x64, comprimida em LZ77)
// para a escolha do inicial e o ícone animado do menu (32x32, 2 quadros) para o Pokémon
// que segue o personagem.

const ROM_BASE = 0x08000000;
export const SPECIES = { BULBASAUR: 1, CHARMANDER: 4, SQUIRTLE: 7 } as const;

const u32 = (rom: Uint8Array, at: number) =>
  (rom[at] | (rom[at + 1] << 8) | (rom[at + 2] << 16) | (rom[at + 3] << 24)) >>> 0;
const romOffset = (pointer: number) => pointer - ROM_BASE;

/** Descompressão LZ77 da BIOS do GBA (tipo 0x10). */
export function lz77(rom: Uint8Array, at: number): Uint8Array {
  const header = u32(rom, at);
  if ((header & 0xff) !== 0x10) throw new Error('não é LZ77');
  const out = new Uint8Array(header >>> 8);
  let src = at + 4;
  let dst = 0;
  while (dst < out.length) {
    const flags = rom[src++];
    for (let bit = 7; bit >= 0 && dst < out.length; bit--) {
      if ((flags & (1 << bit)) === 0) {
        out[dst++] = rom[src++];
        continue;
      }
      const length = (rom[src] >> 4) + 3;
      const distance = (((rom[src] & 0x0f) << 8) | rom[src + 1]) + 1;
      src += 2;
      for (let i = 0; i < length && dst < out.length; i++, dst++) out[dst] = out[dst - distance];
    }
  }
  return out;
}

function palette(bytes: Uint8Array, at = 0): number[][] {
  return Array.from({ length: 16 }, (_, i) => {
    const color = bytes[at + i * 2] | (bytes[at + i * 2 + 1] << 8);
    return [(color & 31) << 3, ((color >> 5) & 31) << 3, ((color >> 10) & 31) << 3];
  });
}

/** Desenha uma imagem 4bpp em tiles 8x8 (linha por linha de tiles) num canvas. */
function drawTiles(pixels: Uint8Array, at: number, width: number, height: number, colors: number[][]): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const image = new ImageData(width, height);
  const tilesPerRow = width / 8;
  for (let tile = 0; tile < (width / 8) * (height / 8); tile++) {
    for (let py = 0; py < 8; py++) {
      for (let px = 0; px < 8; px++) {
        const byte = pixels[at + tile * 32 + py * 4 + (px >> 1)];
        const index = px & 1 ? byte >> 4 : byte & 15;
        if (index === 0) continue;
        const x = (tile % tilesPerRow) * 8 + px;
        const y = Math.floor(tile / tilesPerRow) * 8 + py;
        const i = (y * width + x) * 4;
        image.data.set([...colors[index], 255], i);
      }
    }
  }
  canvas.getContext('2d')!.putImageData(image, 0, 0);
  return canvas;
}

/** Foto de frente (64x64) de uma espécie. */
export function decodeFrontPic(rom: Uint8Array, species: number, sym: FireRedSymbols = activeSymbols()): HTMLCanvasElement {
  const picEntry = romOffset(sym.gMonFrontPicTable) + species * 8; // CompressedSpriteSheet
  const palEntry = romOffset(sym.gMonPaletteTable) + species * 8; // CompressedSpritePalette
  const pixels = lz77(rom, romOffset(u32(rom, picEntry)));
  const colors = palette(lz77(rom, romOffset(u32(rom, palEntry))));
  return drawTiles(pixels, 0, 64, 64, colors);
}

/** Ícones animados do menu (32x32, 2 quadros), com cache por espécie. */
export class MonIcons {
  private readonly cache = new Map<number, [HTMLCanvasElement, HTMLCanvasElement]>();

  constructor(
    private readonly rom: Uint8Array,
    private readonly sym: FireRedSymbols = activeSymbols(),
  ) {}

  get(species: number): [HTMLCanvasElement, HTMLCanvasElement] | null {
    if (species <= 0 || species >= 440) return null;
    const cached = this.cache.get(species);
    if (cached) return cached;
    try {
      const { rom, sym } = this;
      const pixels = romOffset(u32(rom, romOffset(sym.gMonIconTable) + species * 4));
      const paletteIndex = rom[romOffset(sym.gMonIconPaletteIndices) + species];
      const paletteData = romOffset(u32(rom, romOffset(sym.gMonIconPaletteTable) + paletteIndex * 8)); // SpritePalette
      const colors = palette(rom, paletteData);
      const frames: [HTMLCanvasElement, HTMLCanvasElement] = [
        drawTiles(rom, pixels, 32, 32, colors),
        drawTiles(rom, pixels + 32 * 16, 32, 32, colors), // 2º quadro: 16 tiles depois
      ];
      this.cache.set(species, frames);
      return frames;
    } catch {
      return null;
    }
  }
}
